import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReaderChapter } from '@/services/booksApi';
import { buildMountedChapterIndex } from '@/utils/ordinalSync';
import { estimateChapterHeight, LruCache, parseChapterFragment } from '@/utils/readerFragments';
import { resolveAssetUrl } from '@/utils/offlineAssets';
import {
  registerMountedChapterIndex,
  setActiveReaderRoot,
  unregisterMountedChapterIndex,
} from '@/utils/readerDomRegistry';

interface UseChapterFragmentsArgs {
  bookId: string | null;
  chapters: ReaderChapter[];
  currentChapterIndex: number;
  cssUrls: string[];
  shadowRootRef: RefObject<ShadowRoot | null>;
  readerRootRef: RefObject<HTMLDivElement | null>;
  scrollContainerRef: RefObject<HTMLElement | null>;
  shadowReadyVersion: number;
  isInteractive: boolean;
  // Set while an explicit chapter-menu navigation is pending; scroll compensation is skipped so it doesn't fight the final centering pass.
  pendingSelectionChapterIdRef?: RefObject<string | null>;
}

export interface UseChapterFragmentsResult {
  documentVersion: number;
  isLoading: boolean;
  isCssReady: boolean;
  error: string | null;
  hasReadableContent: boolean;
  neighborWarning: string | null;
  indexError: string | null;
  layoutReadiness: {
    chapterId: string;
    generation: number;
    status: 'loading' | 'settled';
  } | null;
  retryCurrentChapter: () => void;
}

const HTML_CACHE_LIMIT = 5;
const MAX_MOUNTED_CHAPTERS = 5;
const MOUNT_RADIUS = 1;
const PREFETCH_RADIUS = 2;
const FRAGMENT_FETCH_TIMEOUT_MS = 15_000;
const PROXIMITY_MARGIN_PX = 1_500;
const PROXIMITY_RETRY_DELAYS_MS = [500, 1_500] as const;

class ChapterRequestError extends Error {
  constructor(message: string, readonly retryable: boolean) {
    super(message);
    this.name = 'ChapterRequestError';
  }
}

interface RetryWaiter {
  timeoutId: number;
  resolve: (shouldRetry: boolean) => void;
}

export const useChapterFragments = ({
  bookId,
  chapters,
  currentChapterIndex,
  cssUrls,
  shadowRootRef,
  readerRootRef,
  scrollContainerRef,
  shadowReadyVersion,
  isInteractive,
  pendingSelectionChapterIdRef,
}: UseChapterFragmentsArgs): UseChapterFragmentsResult => {
  const [documentVersion, setDocumentVersion] = useState(0);
  const [isCurrentLoading, setIsCurrentLoading] = useState(false);
  const [isCssReady, setIsCssReady] = useState(false);
  const [currentError, setCurrentError] = useState<{ chapterId: string; message: string } | null>(null);
  const [retryVersion, setRetryVersion] = useState(0);
  const [slotVersion, setSlotVersion] = useState(0);
  const [hasReadableContent, setHasReadableContent] = useState(false);
  const [neighborWarning, setNeighborWarning] = useState<string | null>(null);
  const [layoutReadiness, setLayoutReadiness] = useState<UseChapterFragmentsResult['layoutReadiness']>(null);
  const [, setIndexErrorVersion] = useState(0);
  const slotsRef = useRef<Map<string, HTMLDivElement>>(new Map());
  const mountedRef = useRef<Set<string>>(new Set());
  const measuredHeightsRef = useRef<Map<string, number>>(new Map());
  const observersRef = useRef<Map<string, ResizeObserver>>(new Map());
  const htmlCacheRef = useRef(new LruCache<string, string>(HTML_CACHE_LIMIT));
  const requestsRef = useRef<Map<string, { controller: AbortController; promise: Promise<string> }>>(new Map());
  const mountAttemptsRef = useRef<Map<string, Promise<void>>>(new Map());
  const proximityMountsRef = useRef<Map<string, Promise<void>>>(new Map());
  const proximityObserverRef = useRef<IntersectionObserver | null>(null);
  const proximityIntersectingRef = useRef<Set<string>>(new Set());
  const retryWaitersRef = useRef<Map<string, Set<RetryWaiter>>>(new Map());
  const lifecycleGenerationRef = useRef(0);
  const layoutGenerationRef = useRef(0);
  const currentChapterIndexRef = useRef(currentChapterIndex);
  const reconcileMountedChaptersRef = useRef<() => void>(() => undefined);
  const indexErrorsRef = useRef<Map<string, string>>(new Map());
  const cssKey = useMemo(() => JSON.stringify(cssUrls.filter(Boolean)), [cssUrls]);
  const normalizedCssUrls = useMemo(() => JSON.parse(cssKey) as string[], [cssKey]);
  currentChapterIndexRef.current = currentChapterIndex;

  const waitForSelectedLayout = useCallback(async (
    chapter: ReaderChapter,
    mountedIndices: Set<number>,
    generation: number,
  ): Promise<boolean> => {
    const isCurrent = () =>
      generation === layoutGenerationRef.current &&
      chapter.id === chapters[currentChapterIndexRef.current]?.id;
    if (!isCurrent()) return false;

    if (typeof document !== 'undefined' && document.fonts?.ready) {
      try {
        await document.fonts.ready;
      } catch {
        // A failed font must not permanently block readable chapter content.
      }
    }
    if (!isCurrent()) return false;

    const relevantImages = Array.from(mountedIndices)
      .map((index) => slotsRef.current.get(chapters[index]?.id ?? ''))
      .filter((slot): slot is HTMLDivElement => Boolean(slot))
      .flatMap((slot) => Array.from(slot.querySelectorAll<HTMLImageElement>('img')));
    await Promise.all(relevantImages.map((image) => {
      if (image.complete) return Promise.resolve();
      return new Promise<void>((resolve) => {
        image.addEventListener('load', () => resolve(), { once: true });
        image.addEventListener('error', () => resolve(), { once: true });
      });
    }));
    if (!isCurrent()) return false;

    const targetSlot = slotsRef.current.get(chapter.id);
    const scrollContainer = scrollContainerRef.current;
    if (!targetSlot || !scrollContainer) return false;

    return new Promise<boolean>((resolve) => {
      let frameId: number | null = null;
      let resizeVersion = 0;
      let sampledResizeVersion = -1;
      let stableFrames = 0;
      let previous: { top: number; height: number; scrollHeight: number; viewportHeight: number } | null = null;
      const observer = typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => { resizeVersion += 1; });
      observer?.observe(targetSlot);
      observer?.observe(scrollContainer);

      const finish = (settled: boolean) => {
        if (frameId !== null) cancelAnimationFrame(frameId);
        observer?.disconnect();
        resolve(settled);
      };
      const sample = () => {
        frameId = null;
        if (!isCurrent()) {
          finish(false);
          return;
        }
        const rect = targetSlot.getBoundingClientRect();
        const next = {
          top: rect.top,
          height: rect.height,
          scrollHeight: scrollContainer.scrollHeight,
          viewportHeight: scrollContainer.clientHeight,
        };
        const unchanged = Boolean(
          previous &&
          sampledResizeVersion === resizeVersion &&
          Math.abs(previous.top - next.top) <= 0.5 &&
          Math.abs(previous.height - next.height) <= 0.5 &&
          Math.abs(previous.scrollHeight - next.scrollHeight) <= 0.5 &&
          Math.abs(previous.viewportHeight - next.viewportHeight) <= 0.5
        );
        stableFrames = unchanged ? stableFrames + 1 : 0;
        previous = next;
        sampledResizeVersion = resizeVersion;
        if (stableFrames >= 2) {
          finish(true);
          return;
        }
        frameId = requestAnimationFrame(sample);
      };
      frameId = requestAnimationFrame(sample);
    });
  }, [chapters, scrollContainerRef]);

  const retryCurrentChapter = useCallback(() => {
    const chapter = chapters[currentChapterIndex];
    if (chapter) {
      htmlCacheRef.current.delete(chapter.htmlUrl);
      setRetryVersion((value) => value + 1);
    }
  }, [chapters, currentChapterIndex]);

  const cancelRetryWaiters = useCallback((chapterId?: string) => {
    const chapterIds = chapterId ? [chapterId] : Array.from(retryWaitersRef.current.keys());
    for (const id of chapterIds) {
      const waiters = retryWaitersRef.current.get(id);
      if (!waiters) continue;
      retryWaitersRef.current.delete(id);
      for (const waiter of waiters) {
        window.clearTimeout(waiter.timeoutId);
        waiter.resolve(false);
      }
    }
  }, []);

  const waitForProximityRetry = useCallback((chapterId: string, delayMs: number): Promise<boolean> => {
    if (!proximityIntersectingRef.current.has(chapterId)) {
      return Promise.resolve(false);
    }
    return new Promise<boolean>((resolve) => {
      const waiters = retryWaitersRef.current.get(chapterId) ?? new Set<RetryWaiter>();
      let waiter: RetryWaiter;
      const timeoutId = window.setTimeout(() => {
        waiters.delete(waiter);
        if (waiters.size === 0) retryWaitersRef.current.delete(chapterId);
        resolve(proximityIntersectingRef.current.has(chapterId));
      }, delayMs);
      waiter = { timeoutId, resolve };
      waiters.add(waiter);
      retryWaitersRef.current.set(chapterId, waiters);
    });
  }, []);

  const loadHtml = useCallback((chapter: ReaderChapter): Promise<string> => {
    const cached = htmlCacheRef.current.get(chapter.htmlUrl);
    if (cached !== undefined) {
      return Promise.resolve(cached);
    }
    const pending = requestsRef.current.get(chapter.htmlUrl);
    if (pending) {
      return pending.promise;
    }
    const controller = new AbortController();
    let didTimeout = false;
    const timeoutId = window.setTimeout(() => {
      didTimeout = true;
      controller.abort();
    }, FRAGMENT_FETCH_TIMEOUT_MS);
    let promise: Promise<string>;
    // Keyed above by chapter.htmlUrl on purpose: the cache identity stays the
    // CDN URL, so a book that downloads mid-session does not orphan its entries.
    promise = fetch(resolveAssetUrl(chapter.htmlUrl), { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          const retryable = response.status === 408 || response.status === 429 || response.status >= 500;
          throw new ChapterRequestError(
            `Failed to fetch chapter '${chapter.title}': ${response.statusText || response.status}`,
            retryable,
          );
        }
        return response.text();
      })
      .then((html) => {
        htmlCacheRef.current.set(chapter.htmlUrl, html);
        return html;
      })
      .catch((error: unknown) => {
        if (didTimeout) {
          throw new ChapterRequestError(
            `Chapter request timed out after ${FRAGMENT_FETCH_TIMEOUT_MS / 1000} seconds`,
            true,
          );
        }
        if (error instanceof ChapterRequestError || (error instanceof DOMException && error.name === 'AbortError')) {
          throw error;
        }
        if (error instanceof TypeError) {
          throw new ChapterRequestError(error.message || 'Chapter request failed', true);
        }
        throw error;
      })
      .finally(() => {
        window.clearTimeout(timeoutId);
        if (requestsRef.current.get(chapter.htmlUrl)?.promise === promise) {
          requestsRef.current.delete(chapter.htmlUrl);
        }
      });
    requestsRef.current.set(chapter.htmlUrl, { controller, promise });
    return promise;
  }, []);

  useEffect(() => {
    const readerRoot = readerRootRef.current;
    if (!readerRoot) {
      return;
    }
    readerRoot.classList.toggle('reader-word-sync--interactive', isInteractive);
  }, [isInteractive, readerRootRef, shadowReadyVersion]);

  useEffect(() => {
    const readerRoot = readerRootRef.current;
    if (!readerRoot) return;
    readerRoot.style.visibility = isCssReady ? 'visible' : 'hidden';
    readerRoot.setAttribute('aria-busy', isCssReady ? 'false' : 'true');
    return () => {
      readerRoot.style.removeProperty('visibility');
      readerRoot.removeAttribute('aria-busy');
    };
  }, [isCssReady, readerRootRef, shadowReadyVersion]);

  useEffect(() => {
    const readerRoot = readerRootRef.current;
    if (!readerRoot || !bookId) {
      return;
    }

    lifecycleGenerationRef.current += 1;
    requestsRef.current.forEach(({ controller }) => controller.abort());
    requestsRef.current.clear();
    mountAttemptsRef.current.clear();
    proximityMountsRef.current.clear();
    proximityObserverRef.current?.disconnect();
    proximityObserverRef.current = null;
    proximityIntersectingRef.current.clear();
    cancelRetryWaiters();
    observersRef.current.forEach((observer) => observer.disconnect());
    observersRef.current.clear();
    mountedRef.current.forEach(unregisterMountedChapterIndex);
    mountedRef.current.clear();
    slotsRef.current.clear();
    htmlCacheRef.current.clear();
    measuredHeightsRef.current.clear();
    indexErrorsRef.current.clear();
    setHasReadableContent(false);
    setNeighborWarning(null);
    readerRoot.replaceChildren();

    for (const chapter of chapters) {
      const slot = document.createElement('div');
      slot.className = 'reader-chapter-slot';
      slot.dataset.chapterSlotId = chapter.id;
      slot.dataset.chapterMounted = 'false';
      slot.style.minHeight = `${estimateChapterHeight(chapter)}px`;
      slot.setAttribute('aria-hidden', 'true');
      readerRoot.appendChild(slot);
      slotsRef.current.set(chapter.id, slot);
    }
    setActiveReaderRoot(readerRoot);
    setDocumentVersion((value) => value + 1);
    setSlotVersion((value) => value + 1);

    return () => {
      lifecycleGenerationRef.current += 1;
      requestsRef.current.forEach(({ controller }) => controller.abort());
      requestsRef.current.clear();
      mountAttemptsRef.current.clear();
      proximityMountsRef.current.clear();
      proximityObserverRef.current?.disconnect();
      proximityObserverRef.current = null;
      proximityIntersectingRef.current.clear();
      cancelRetryWaiters();
      observersRef.current.forEach((observer) => observer.disconnect());
      observersRef.current.clear();
      mountedRef.current.forEach(unregisterMountedChapterIndex);
      mountedRef.current.clear();
      slotsRef.current.clear();
      readerRoot.replaceChildren();
      setActiveReaderRoot(null);
    };
  }, [bookId, cancelRetryWaiters, chapters, readerRootRef, shadowReadyVersion]);

  useEffect(() => {
    const shadowRoot = shadowRootRef.current;
    if (!shadowRoot || !bookId) {
      setIsCssReady(false);
      return;
    }
    shadowRoot.querySelectorAll('link[data-reader-css="1"]').forEach((node) => node.remove());
    const urls = normalizedCssUrls;
    if (urls.length === 0) {
      setIsCssReady(true);
      return;
    }

    let cancelled = false;
    let pending = urls.length;
    const links: HTMLLinkElement[] = [];
    setIsCssReady(false);
    const settle = (event: Event) => {
      if (event.type === 'error') {
        console.warn('[reader] A bundle stylesheet failed to load; displaying the available content.');
      }
      pending -= 1;
      if (!cancelled && pending === 0) {
        setIsCssReady(true);
      }
    };
    const interactionStyle = shadowRoot.getElementById('reader-interaction-styles');
    for (const url of urls) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = resolveAssetUrl(url);
      link.dataset.readerCss = '1';
      link.addEventListener('load', settle, { once: true });
      link.addEventListener('error', settle, { once: true });
      shadowRoot.insertBefore(link, interactionStyle);
      links.push(link);
    }
    return () => {
      cancelled = true;
      links.forEach((link) => link.remove());
    };
  }, [bookId, cssKey, normalizedCssUrls, shadowReadyVersion, shadowRootRef]);

  const observeMountedChapter = useCallback((chapter: ReaderChapter, root: HTMLElement, slot: HTMLDivElement) => {
    observersRef.current.get(chapter.id)?.disconnect();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect.height;
      if (!height || !Number.isFinite(height)) return;
      const previous = measuredHeightsRef.current.get(chapter.id);
      measuredHeightsRef.current.set(chapter.id, height);
      const scrollContainer = scrollContainerRef.current;
      if (previous && scrollContainer && !pendingSelectionChapterIdRef?.current) {
        const slotRect = slot.getBoundingClientRect();
        const scrollRect = scrollContainer.getBoundingClientRect();
        if (slotRect.bottom <= scrollRect.top) {
          scrollContainer.scrollTop += height - previous;
        }
      }
    });
    observer.observe(root);
    observersRef.current.set(chapter.id, observer);
  }, [scrollContainerRef, pendingSelectionChapterIdRef]);

  const unmountChapter = useCallback((chapter: ReaderChapter): void => {
    if (!mountedRef.current.has(chapter.id)) return;
    const generation = lifecycleGenerationRef.current;
    const slot = slotsRef.current.get(chapter.id);
    if (!slot) return;
    const scrollContainer = scrollContainerRef.current;
    const beforeRect = slot.getBoundingClientRect();
    const beforeHeight = beforeRect.height;
    const replacementHeight = beforeHeight > 0
      ? beforeHeight
      : measuredHeightsRef.current.get(chapter.id) ?? estimateChapterHeight(chapter);
    const wasAboveViewport = Boolean(
      scrollContainer && beforeRect.bottom <= scrollContainer.getBoundingClientRect().top
    );

    observersRef.current.get(chapter.id)?.disconnect();
    observersRef.current.delete(chapter.id);
    unregisterMountedChapterIndex(chapter.id);
    if (indexErrorsRef.current.delete(chapter.id)) {
      setIndexErrorVersion((value) => value + 1);
    }
    mountedRef.current.delete(chapter.id);
    measuredHeightsRef.current.set(chapter.id, replacementHeight);
    slot.replaceChildren();
    slot.dataset.chapterMounted = 'false';
    slot.setAttribute('aria-hidden', 'true');
    slot.style.minHeight = `${replacementHeight}px`;
    proximityObserverRef.current?.observe(slot);
    if (mountedRef.current.size === 0) setHasReadableContent(false);

    requestAnimationFrame(() => {
      if (
        generation !== lifecycleGenerationRef.current ||
        !wasAboveViewport ||
        !scrollContainer ||
        beforeHeight <= 0 ||
        pendingSelectionChapterIdRef?.current
      ) return;
      const afterHeight = slot.getBoundingClientRect().height;
      if (afterHeight > 0) scrollContainer.scrollTop += afterHeight - beforeHeight;
    });
  }, [scrollContainerRef, pendingSelectionChapterIdRef]);

  const mountChapter = useCallback((chapter: ReaderChapter): Promise<void> => {
    if (mountedRef.current.has(chapter.id)) return Promise.resolve();
    const pending = mountAttemptsRef.current.get(chapter.id);
    if (pending) return pending;

    const generation = lifecycleGenerationRef.current;
    let promise: Promise<void>;
    promise = loadHtml(chapter)
      .then((html) => {
        if (generation !== lifecycleGenerationRef.current || mountedRef.current.has(chapter.id)) return;
        const slot = slotsRef.current.get(chapter.id);
        if (!slot) return;
        const root = parseChapterFragment(html, chapter);
        const scrollContainer = scrollContainerRef.current;
        const beforeHeight = slot.getBoundingClientRect().height;
        const wasAboveViewport = Boolean(
          scrollContainer && slot.getBoundingClientRect().bottom <= scrollContainer.getBoundingClientRect().top
        );
        slot.replaceChildren(root);
        slot.style.removeProperty('min-height');
        slot.dataset.chapterMounted = 'true';
        slot.removeAttribute('aria-hidden');
        mountedRef.current.add(chapter.id);
        cancelRetryWaiters(chapter.id);
        setHasReadableContent(true);
        try {
          registerMountedChapterIndex(chapter.id, buildMountedChapterIndex(root));
          if (indexErrorsRef.current.delete(chapter.id)) {
            setIndexErrorVersion((value) => value + 1);
          }
        } catch (error) {
          console.warn(`[reader] Could not build ordinal index for chapter '${chapter.id}'.`, error);
          indexErrorsRef.current.set(
            chapter.id,
            error instanceof Error ? error.message : 'Could not build the chapter ordinal index',
          );
          setIndexErrorVersion((value) => value + 1);
        }
        observeMountedChapter(chapter, root, slot);
        requestAnimationFrame(() => {
          if (
            generation !== lifecycleGenerationRef.current ||
            !wasAboveViewport ||
            !scrollContainer ||
            pendingSelectionChapterIdRef?.current
          ) return;
          const afterHeight = slot.getBoundingClientRect().height;
          if (beforeHeight > 0 && afterHeight > 0) {
            scrollContainer.scrollTop += afterHeight - beforeHeight;
          }
        });
        queueMicrotask(() => reconcileMountedChaptersRef.current());
      })
      .finally(() => {
        if (mountAttemptsRef.current.get(chapter.id) === promise) {
          mountAttemptsRef.current.delete(chapter.id);
        }
      });
    mountAttemptsRef.current.set(chapter.id, promise);
    return promise;
  }, [cancelRetryWaiters, loadHtml, observeMountedChapter, scrollContainerRef, pendingSelectionChapterIdRef]);

  const requestProximityMount = useCallback((chapter: ReaderChapter): Promise<void> => {
    if (mountedRef.current.has(chapter.id)) return Promise.resolve();
    const pending = proximityMountsRef.current.get(chapter.id);
    if (pending) return pending;
    const generation = lifecycleGenerationRef.current;
    const promise = (async () => {
      for (let attempt = 0; ; attempt += 1) {
        try {
          await mountChapter(chapter);
          return;
        } catch (error) {
          const delay = PROXIMITY_RETRY_DELAYS_MS[attempt];
          const canRetry =
            error instanceof ChapterRequestError &&
            error.retryable &&
            delay !== undefined &&
            generation === lifecycleGenerationRef.current &&
            proximityIntersectingRef.current.has(chapter.id);
          if (!canRetry) throw error;
          if (!await waitForProximityRetry(chapter.id, delay)) return;
        }
      }
    })();
    proximityMountsRef.current.set(chapter.id, promise);
    const clear = () => {
      if (proximityMountsRef.current.get(chapter.id) === promise) {
        proximityMountsRef.current.delete(chapter.id);
      }
    };
    promise.then(clear, (error: unknown) => {
      clear();
      if (
        generation === lifecycleGenerationRef.current &&
        proximityIntersectingRef.current.has(chapter.id) &&
        !(error instanceof DOMException && error.name === 'AbortError')
      ) {
        setNeighborWarning('Some nearby chapters could not be prepared. The current chapter remains available.');
      }
    });
    return promise;
  }, [mountChapter, waitForProximityRetry]);

  const reconcileMountedChapters = useCallback(() => {
    if (mountedRef.current.size <= MAX_MOUNTED_CHAPTERS) return;
    const protectedChapterIds = new Set(proximityIntersectingRef.current);
    const currentChapter = chapters[currentChapterIndexRef.current];
    if (currentChapter) protectedChapterIds.add(currentChapter.id);
    mountAttemptsRef.current.forEach((_promise, chapterId) => protectedChapterIds.add(chapterId));
    proximityMountsRef.current.forEach((_promise, chapterId) => protectedChapterIds.add(chapterId));

    // Set iteration order is mount order, so this evicts the oldest safe
    // fragments first without maintaining a second LRU structure.
    for (const chapterId of Array.from(mountedRef.current)) {
      if (mountedRef.current.size <= MAX_MOUNTED_CHAPTERS) break;
      if (protectedChapterIds.has(chapterId)) continue;
      const chapter = chapters.find((candidate) => candidate.id === chapterId);
      if (chapter) unmountChapter(chapter);
    }
  }, [chapters, unmountChapter]);
  reconcileMountedChaptersRef.current = reconcileMountedChapters;

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (
      !bookId ||
      !scrollContainer ||
      slotsRef.current.size === 0 ||
      typeof IntersectionObserver === 'undefined'
    ) return;
    const chaptersById = new Map(chapters.map((chapter) => [chapter.id, chapter]));
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const chapterId = (entry.target as HTMLElement).dataset.chapterSlotId;
        if (!chapterId) continue;
        if (!entry.isIntersecting) {
          proximityIntersectingRef.current.delete(chapterId);
          cancelRetryWaiters(chapterId);
          continue;
        }
        proximityIntersectingRef.current.add(chapterId);
        const chapter = chaptersById.get(chapterId);
        if (chapter && !mountedRef.current.has(chapterId)) void requestProximityMount(chapter);
      }
      reconcileMountedChaptersRef.current();
    }, {
      root: scrollContainer,
      rootMargin: `${PROXIMITY_MARGIN_PX}px 0px`,
      threshold: 0,
    });
    proximityObserverRef.current = observer;
    slotsRef.current.forEach((slot) => observer.observe(slot));
    return () => {
      observer.disconnect();
      if (proximityObserverRef.current === observer) proximityObserverRef.current = null;
      proximityIntersectingRef.current.clear();
      cancelRetryWaiters();
    };
  }, [bookId, cancelRetryWaiters, chapters, requestProximityMount, scrollContainerRef, slotVersion]);

  useEffect(() => {
    const currentChapter = chapters[currentChapterIndex];
    if (!bookId || !currentChapter || slotsRef.current.size === 0) return;
    let cancelled = false;
    layoutGenerationRef.current += 1;
    const layoutGeneration = layoutGenerationRef.current;
    setLayoutReadiness({ chapterId: currentChapter.id, generation: layoutGeneration, status: 'loading' });
    const mountedIndices = new Set<number>();
    for (let index = currentChapterIndex - MOUNT_RADIUS; index <= currentChapterIndex + MOUNT_RADIUS; index += 1) {
      if (index >= 0 && index < chapters.length) mountedIndices.add(index);
    }

    setIsCurrentLoading(!mountedRef.current.has(currentChapter.id));
    setCurrentError(null);
    const pendingProximityMount = proximityMountsRef.current.get(currentChapter.id);
    void (pendingProximityMount ?? mountChapter(currentChapter))
      .then(async () => {
        if (cancelled) return;
        setIsCurrentLoading(false);
        const neighborResults = await Promise.allSettled(
          Array.from(mountedIndices)
            .filter((index) => index !== currentChapterIndex)
            .map((index) => mountChapter(chapters[index])),
        );
        if (cancelled) return;
        setNeighborWarning(
          neighborResults.some((result) => result.status === 'rejected')
            ? 'Some nearby chapters could not be prepared. The current chapter remains available.'
            : null,
        );
        reconcileMountedChaptersRef.current();
        const didSettle = await waitForSelectedLayout(currentChapter, mountedIndices, layoutGeneration);
        if (cancelled || !didSettle) return;
        // Only the selected chapter advances the document version. Background
        // mounts must not re-run playback positioning while the user is reading elsewhere.
        setDocumentVersion((value) => value + 1);
        setLayoutReadiness({ chapterId: currentChapter.id, generation: layoutGeneration, status: 'settled' });
        for (let index = currentChapterIndex - PREFETCH_RADIUS; index <= currentChapterIndex + PREFETCH_RADIUS; index += 1) {
          if (index >= 0 && index < chapters.length && !mountedIndices.has(index)) {
            void loadHtml(chapters[index]).catch((error: unknown) => {
              htmlCacheRef.current.delete(chapters[index].htmlUrl);
              if (!cancelled && !(error instanceof DOMException && error.name === 'AbortError')) {
                setNeighborWarning('Some nearby chapters could not be prepared. The current chapter remains available.');
              }
            });
          }
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setIsCurrentLoading(false);
        setCurrentError({
          chapterId: currentChapter.id,
          message: error instanceof Error ? error.message : 'Failed to load chapter fragment',
        });
      });

    return () => {
      cancelled = true;
      if (layoutGenerationRef.current === layoutGeneration) layoutGenerationRef.current += 1;
    };
  }, [bookId, chapters, currentChapterIndex, loadHtml, mountChapter, retryVersion, slotVersion, waitForSelectedLayout]);

  const currentChapterId = chapters[currentChapterIndex]?.id;
  return {
    documentVersion,
    isLoading: isCurrentLoading,
    isCssReady,
    error: currentError?.chapterId === currentChapterId ? currentError.message : null,
    hasReadableContent,
    neighborWarning,
    indexError: currentChapterId ? indexErrorsRef.current.get(currentChapterId) ?? null : null,
    layoutReadiness,
    retryCurrentChapter,
  };
};
